/**
 * The confirmation step in front of a destructive write (Feature: granular-crud-api, task 14.1;
 * Requirements 11.6, 11.8; Design: §11).
 *
 * Deleting a Check cascades — its Check_State, heartbeat item, schedule membership, and firing Alerts
 * all go with it — and deleting an Alert_Rule resolves every Alert firing for it. Neither is
 * recoverable from the dashboard, so both go through this dialog. It is also where the adoption notice
 * belongs for a manifest-managed target (Req 11.8): the `children` slot renders above the buttons, so
 * the consequence is stated at the moment of decision rather than on the page behind it.
 *
 * A failed delete keeps the dialog open with the returned error (Req 11.6) — the list or detail view
 * behind it is untouched, so dismissing the dialog restores exactly the pre-request state.
 */

import type { ReactNode } from 'react';

export interface ConfirmDialogProps {
  title: string;
  /** The plain-language consequence of confirming. */
  message: ReactNode;
  confirmLabel: string;
  /** The delete request is in flight. */
  busy?: boolean;
  /** The last attempt's error, shown in place so the dialog stays open (Req 11.6). */
  errorMessage?: string;
  /**
   * Hold the confirm back for a reason in {@link children} (Feature: terraform-provider, task 9.2; Req
   * 13.3) — today, an unticked Terraform acknowledgement. Distinct from {@link busy}: cancel stays
   * available, because the point is to make the user choose, not to trap them in the dialog.
   */
  confirmDisabled?: boolean;
  /** Extra content above the actions — the provenance notice for the target (Req 11.8, 13.1, 13.2). */
  children?: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  busy = false,
  errorMessage,
  confirmDisabled = false,
  children,
  onConfirm,
  onCancel,
}: ConfirmDialogProps): JSX.Element {
  return (
    <div className="dialog-backdrop">
      <div role="dialog" aria-modal="true" aria-label={title} className="dialog card">
        <h2>{title}</h2>
        <p>{message}</p>
        {children}
        {errorMessage !== undefined ? (
          <div role="alert" className="error-panel">
            <p>{errorMessage}</p>
          </div>
        ) : null}
        <div className="form-actions">
          <button
            type="button"
            className="danger"
            disabled={busy || confirmDisabled}
            // Guarded as well as disabled: the requirement is that no delete is *issued* before the
            // acknowledgement (Req 13.3), and a disabled attribute is the presentation of that rule,
            // not the rule. A programmatic click, or a click racing a re-render, still gets nowhere.
            onClick={() => {
              if (busy || confirmDisabled) return;
              onConfirm();
            }}
          >
            {confirmLabel}
          </button>
          <button type="button" className="secondary" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
